import { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Mail, MapPin, Phone, Youtube } from "lucide-react";
import { BkaWordmark } from "@/components/BkaLogo";
import { api, apiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const COL_ONE = [
  { label: "About", href: "/about" },
  { label: "Kidney Health", href: "/kidney-health" },
  { label: "Our Efforts", href: "/our-efforts" },
  { label: "Support", href: "/get-support" },
];

const COL_TWO = [
  { label: "Blogs", href: "/blog" },
  { label: "Emerald Honours", href: "/emerald-honours" },
  { label: "Testimonials", href: "/testimonials" },
  { label: "Contact", href: "/contact" },
];

export function PublicFooter() {
  const { s } = useSite();
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ status: "idle", message: "" });

  const phone = (s("site.phone") || "").trim();
  const emailAddress = (s("site.email") || "").trim();
  const address = (s("site.address") || "").trim();

  const socials = [
    { key: "social.facebook_url", Icon: Facebook, label: "Facebook" },
    { key: "social.instagram_url", Icon: Instagram, label: "Instagram" },
    { key: "social.youtube_url", Icon: Youtube, label: "YouTube" },
    { key: "social.linkedin_url", Icon: Linkedin, label: "LinkedIn" },
  ].filter((x) => (s(x.key) || "").trim());

  const subscribe = async (e) => {
    e.preventDefault();
    setState({ status: "loading", message: "" });
    try {
      const { data } = await api.post("/public/newsletter", { email, honeypot: "" });
      setState({ status: "ok", message: data.message });
      setEmail("");
    } catch (err) {
      setState({ status: "error", message: apiError(err) });
    }
  };

  return (
    <footer data-testid="public-footer" className="relative overflow-hidden bg-bka-navy text-white">
      <div className="bka-grain absolute inset-0" aria-hidden="true" />

      <div className="bka-container relative grid gap-10 py-14 lg:grid-cols-[1.15fr_1.35fr_1.1fr] lg:gap-12">
        {/* brand + contact */}
        <div>
          <BkaWordmark tone="dark" />
          <ul className="mt-6 space-y-3 text-[13px] text-white/75">
            {address && (
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-bka-orangeLight" />
                <span data-testid="footer-address">{address}</span>
              </li>
            )}
            {phone && (
              <li className="flex items-start gap-2.5">
                <Phone className="mt-0.5 h-3.5 w-3.5 shrink-0 text-bka-orangeLight" />
                <a
                  data-testid="footer-phone"
                  href={`tel:${phone.replace(/[^\d+]/g, "")}`}
                  className="transition-colors hover:text-white"
                >
                  {phone}
                </a>
              </li>
            )}
            {emailAddress && (
              <li className="flex items-start gap-2.5">
                <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0 text-bka-orangeLight" />
                <a
                  data-testid="footer-email"
                  href={`mailto:${emailAddress}`}
                  className="break-all transition-colors hover:text-white"
                >
                  {emailAddress}
                </a>
              </li>
            )}
          </ul>
        </div>

        {/* quick links, two columns as per the mockup */}
        <div>
          <h3 className="bka-overline text-bka-orangeLight">Quick Links</h3>
          <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 text-[13px]">
            {[...COL_ONE, ...COL_TWO].map((l) => (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="text-white/75 transition-colors hover:text-bka-orangeLight"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>

        {/* newsletter */}
        <div>
          <h3 className="bka-overline text-bka-orangeLight">Join Our Newsletter</h3>
          <form onSubmit={subscribe} className="mt-5" data-testid="footer-newsletter-form">
            <label htmlFor="footer-newsletter-email" className="sr-only">
              Your email address
            </label>
            <div className="flex gap-2">
              <input
                id="footer-newsletter-email"
                data-testid="footer-newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="min-w-0 flex-1 rounded-lg border border-white/20 bg-white/10 px-3 py-2.5 text-[13px] text-white placeholder:text-white/50 focus:border-bka-orange focus:outline-none"
              />
              <button
                type="submit"
                data-testid="footer-newsletter-submit"
                disabled={state.status === "loading"}
                className="rounded-lg bg-bka-emerald px-4 py-2.5 text-[13px] font-bold transition-colors hover:bg-bka-emeraldDark disabled:opacity-60"
              >
                {state.status === "loading" ? "…" : "Subscribe"}
              </button>
            </div>
            {state.message && (
              <p
                data-testid="footer-newsletter-message"
                className={`mt-3 text-xs ${state.status === "ok" ? "text-bka-aqua" : "text-red-300"}`}
              >
                {state.message}
              </p>
            )}
          </form>

          {socials.length > 0 && (
            <div className="mt-6 flex gap-2.5">
              {socials.map(({ key, Icon, label }) => (
                <a
                  key={key}
                  href={s(key)}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-white/20 transition-colors hover:border-bka-orange hover:text-bka-orangeLight"
                >
                  <Icon className="h-3.5 w-3.5" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="relative border-t border-white/10">
        <div className="bka-container flex flex-col gap-2 py-5 text-[11px] text-white/55 sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} Bahamas Kidney Association. All rights reserved.</p>
          <p className="font-semibold text-white/70">A Healthier People. A Stronger Bahamas.</p>
        </div>
      </div>
    </footer>
  );
}
